import { useEffect, useState } from 'react';

/** Elements that can receive keyboard focus inside a trapped container. */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Skips elements hidden via `display`/`visibility`/`aria-hidden`. Layout checks like
 * `offsetParent` are avoided — they also exclude visually hidden but focusable inputs.
 */
const isFocusableElementVisible = (element: HTMLElement) => {
  if (element.hasAttribute('hidden') || element.getAttribute('aria-hidden') === 'true') {
    return false;
  }

  const { display, visibility } = window.getComputedStyle(element);

  return display !== 'none' && visibility !== 'hidden';
};

const getFocusableElements = (container: HTMLElement) =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isFocusableElementVisible);

const DIALOG_SELECTOR = '[role="dialog"], [role="alertdialog"]';

/** Suspend the trap while focus is in a nested (portalled) dialog. Dialogs that contain the container don't count. */
const isFocusInStackedDialog = (container: HTMLElement) => {
  const activeElement = document.activeElement;

  if (!(activeElement instanceof HTMLElement) || container.contains(activeElement)) {
    return false;
  }

  const dialog = activeElement.closest(DIALOG_SELECTOR);

  return dialog !== null && !dialog.contains(container);
};

interface UseFocusTrapOptions {
  /** Trap is only armed while this is true; defaults to true for always-mounted modals. */
  active?: boolean;
  /** Return focus to the element that opened the modal once the trap is released. */
  restoreFocus?: boolean;
}

/**
 * Keeps keyboard focus inside a modal: focus enters the first interactive element,
 * Tab / Shift+Tab cycle within it, and returns to the trigger on close.
 *
 * Returns a callback ref (not a `RefObject`) so it also works for portalled modals,
 * whose container node appears a render or two after the hook first runs.
 *
 * @example
 * const setDialogRef = useFocusTrap<HTMLDivElement>({ active: visible });
 * return <div ref={setDialogRef} role="dialog" aria-modal="true" tabIndex={-1} />;
 */
export const useFocusTrap = <T extends HTMLElement>({
  active = true,
  restoreFocus = true,
}: UseFocusTrapOptions = {}) => {
  const [container, setContainer] = useState<T | null>(null);

  useEffect(() => {
    if (!active || !container) {
      return;
    }

    const triggerElement = document.activeElement as HTMLElement | null;

    // Defer a frame so the modal's enter transition/layout has applied before we
    // measure which of its elements are focusable.
    const frame = requestAnimationFrame(() => {
      const [firstFocusable] = getFocusableElements(container);
      (firstFocusable ?? container).focus();
    });

    // Listen on the document so Tab brings focus back even after it slipped outside.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || isFocusInStackedDialog(container)) {
        return;
      }

      const focusable = getFocusableElements(container);
      const isFocusInside = container.contains(document.activeElement);

      if (focusable.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey) {
        if (!isFocusInside || document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (!isFocusInside || document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleKeyDown, true);

      if (!restoreFocus || !triggerElement?.isConnected) {
        return;
      }

      // Only reclaim focus when the modal still owned it: something inside it (a
      // document panel, another dialog) may have deliberately moved focus elsewhere.
      const activeElement = document.activeElement;
      if (!activeElement || activeElement === document.body || container.contains(activeElement)) {
        triggerElement.focus();
      }
    };
  }, [active, container, restoreFocus]);

  return setContainer;
};
